import { useCallback, useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";

export default function AdminClientsPage() {
  const { user: currentAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // { user, preview } or null
  const [deleting, setDeleting] = useState(false);
  const [bankEdit, setBankEdit] = useState(null); // { user } or null
  const [pwReset, setPwReset] = useState(null); // { user } or null

  const load = useCallback(
    () =>
      api
        .get("/admin/users")
        .then(({ data }) => setUsers(data))
        .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const startDelete = async (u) => {
    setError("");
    try {
      const { data } = await api.get(`/admin/users/${u.id}/cascade-preview`);
      setPendingDelete({ user: u, preview: data });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/admin/users/${pendingDelete.user.id}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-clients-page">
        <div className="br-fade-up">
          <div className="br-overline">Membri</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Clienti <em>e ruoli</em>.
          </h1>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="admin-clients-error">
            {String(error)}
          </div>
        )}

        <div className="mt-10 br-card overflow-x-auto br-fade-up br-delay-1" data-testid="admin-clients-table">
          <table className="w-full text-left">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>Nome</Th>
                <Th>Email</Th>
                <Th>Ruolo</Th>
                <Th>Stato</Th>
                <Th>Creato</Th>
                <Th>Ultimo accesso</Th>
                <Th align="right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = currentAdmin && currentAdmin.id === u.id;
                return (
                  <tr
                    key={u.id}
                    className="border-b"
                    style={{ borderColor: "var(--br-line)" }}
                    data-testid={`client-row-${u.id}`}
                  >
                    <Td className="text-[color:var(--br-parchment)]">
                      {u.name}
                      {isSelf && (
                        <span
                          className="ml-2 font-mono-fin text-[0.58rem] tracking-[0.22em] uppercase border px-2 py-0.5"
                          style={{ borderColor: "var(--br-bronze)", color: "var(--br-bronze)" }}
                        >
                          tu
                        </span>
                      )}
                    </Td>
                    <Td className="font-mono-fin text-[0.85rem]">{u.email}</Td>
                    <Td>
                      <select
                        className="bg-transparent border font-mono-fin text-[0.7rem] uppercase tracking-[0.18em] px-2 py-1"
                        style={{
                          borderColor: "var(--br-line-bright)",
                          color: u.role === "admin" ? "var(--br-bronze)" : "var(--br-text)",
                        }}
                        value={u.role}
                        onChange={(e) => patch(u.id, { role: e.target.value })}
                        data-testid={`client-role-${u.id}`}
                      >
                        <option value="client">client</option>
                        <option value="admin">admin</option>
                      </select>
                    </Td>
                    <Td>
                      <select
                        className="bg-transparent border font-mono-fin text-[0.7rem] uppercase tracking-[0.18em] px-2 py-1"
                        style={{
                          borderColor: "var(--br-line-bright)",
                          color: u.status === "active" ? "var(--br-bronze)" : "var(--br-red)",
                        }}
                        value={u.status}
                        onChange={(e) => patch(u.id, { status: e.target.value })}
                        data-testid={`client-status-${u.id}`}
                      >
                        <option value="active">active</option>
                        <option value="suspended">suspended</option>
                        <option value="closed">closed</option>
                      </select>
                    </Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(u.created_at)}</Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">
                      {u.last_login_at ? formatDate(u.last_login_at) : "—"}
                    </Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => setBankEdit({ user: u })}
                          className="br-btn-outline"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.66rem",
                            borderColor: u.bank_details ? "var(--br-bronze)" : "var(--br-line-bright)",
                            color: u.bank_details ? "var(--br-bronze)" : "var(--br-text-dim)",
                          }}
                          title="Coordinate bancarie"
                          data-testid={`bank-edit-${u.id}`}
                        >
                          {u.bank_details ? "Coordinate ✓" : "Coordinate"}
                        </button>
                        <button
                          onClick={() => setPwReset({ user: u })}
                          disabled={isSelf}
                          className="br-btn-outline"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.66rem",
                            borderColor: isSelf ? "var(--br-line-bright)" : "var(--br-bronze-soft)",
                            color: isSelf ? "var(--br-text-dim)" : "var(--br-bronze-soft)",
                            cursor: isSelf ? "not-allowed" : "pointer",
                            opacity: isSelf ? 0.4 : 1,
                          }}
                          title={isSelf ? "Non puoi resettare la tua password da qui" : "Recupero password"}
                          data-testid={`pwreset-open-${u.id}`}
                        >
                          Reset password
                        </button>
                        <button
                          onClick={() => startDelete(u)}
                          disabled={isSelf}
                          className="br-btn-outline"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.66rem",
                            borderColor: isSelf ? "var(--br-line-bright)" : "var(--br-red)",
                            color: isSelf ? "var(--br-text-dim)" : "var(--br-red)",
                            cursor: isSelf ? "not-allowed" : "pointer",
                            opacity: isSelf ? 0.4 : 1,
                          }}
                          title={isSelf ? "Non puoi eliminare il tuo stesso account" : "Elimina cliente"}
                          data-testid={`delete-user-${u.id}`}
                        >
                          Elimina
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {pendingDelete && (
        <DeleteConfirmModal
          info={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
          loading={deleting}
        />
      )}

      {bankEdit && (
        <BankDetailsModal
          target={bankEdit.user}
          onClose={() => setBankEdit(null)}
          onSaved={() => {
            setBankEdit(null);
            load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {pwReset && (
        <PasswordResetModal
          target={pwReset.user}
          onClose={() => setPwReset(null)}
          onError={(msg) => setError(msg)}
        />
      )}
    </PortalLayout>
  );
}

function BankDetailsModal({ target, onClose, onSaved, onError }) {
  const initial = target.bank_details || {};
  const [form, setForm] = useState({
    beneficiary: initial.beneficiary || "",
    iban: initial.iban || "",
    bic: initial.bic || "",
    bank: initial.bank || "",
    reference_template: initial.reference_template || "",
  });
  const [saving, setSaving] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${target.id}`, { bank_details: form });
      onSaved();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${target.id}`, {
        bank_details: { beneficiary: "", iban: "", bic: "", bank: "", reference_template: "" },
      });
      onSaved();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 overflow-y-auto py-12"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="bank-edit-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[620px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="br-overline">Coordinate bancarie</div>
        <h2 className="font-serif-display text-[2rem] mt-3 leading-[1.1]">
          {target.name || target.email}
        </h2>
        <p className="text-[0.92rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed">
          Coordinate dedicate per i bonifici in ingresso del cliente. Lascia tutti i campi vuoti per
          eliminarle.
        </p>

        <div className="mt-7 space-y-4">
          <Field label="Beneficiario" value={form.beneficiary} onChange={setField("beneficiary")} testid="bank-field-beneficiary" />
          <Field label="IBAN" value={form.iban} onChange={setField("iban")} mono testid="bank-field-iban" />
          <Field label="BIC / SWIFT" value={form.bic} onChange={setField("bic")} mono testid="bank-field-bic" />
          <Field label="Banca" value={form.bank} onChange={setField("bank")} testid="bank-field-bank" />
          <Field
            label="Causale (template)"
            value={form.reference_template}
            onChange={setField("reference_template")}
            mono
            placeholder="es. BR-2026-CL-001 / Conferimento iniziale"
            testid="bank-field-reference"
            hint="Suggerimento mostrato al cliente. Può personalizzarla al momento del bonifico."
          />
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="br-btn-outline"
            data-testid="bank-edit-cancel"
          >
            Annulla
          </button>
          <button
            onClick={clearAll}
            disabled={saving}
            className="br-btn-outline"
            style={{ borderColor: "var(--br-red)", color: "var(--br-red)" }}
            data-testid="bank-edit-clear"
          >
            Svuota
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="br-btn-primary"
            data-testid="bank-edit-save"
          >
            {saving ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, mono = false, placeholder, hint, testid }) {
  return (
    <label className="block">
      <span className="br-overline">{label}</span>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] transition-colors ${
          mono ? "font-mono-fin" : ""
        }`}
        style={{ borderColor: "var(--br-line-bright)" }}
        data-testid={testid}
      />
      {hint && (
        <span className="text-[0.72rem] text-[color:var(--br-text-dim)] mt-1 block">{hint}</span>
      )}
    </label>
  );
}

function DeleteConfirmModal({ info, onCancel, onConfirm, loading }) {
  const { user, preview } = info;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="delete-user-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[520px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="br-overline" style={{ color: "var(--br-red)" }}>
          Operazione irreversibile
        </div>
        <h2 className="font-serif-display text-[2rem] mt-4 leading-[1.1]">
          Eliminare <em>{user.name || user.email}</em>?
        </h2>
        <p className="text-[0.95rem] text-[color:var(--br-text)] mt-4 leading-relaxed">
          Stai per eliminare definitivamente <span className="font-mono-fin text-[color:var(--br-parchment)]">{user.email}</span>{" "}
          e tutti i dati collegati. Questa azione <strong>non può essere annullata</strong>.
        </p>

        <div
          className="mt-6 p-5 border-l-2"
          style={{ borderColor: "var(--br-red)", background: "rgba(179,107,107,0.04)" }}
        >
          <div className="br-overline" style={{ color: "var(--br-red)" }}>
            Verranno eliminati anche
          </div>
          <table className="w-full mt-3 font-mono-fin text-[0.92rem]">
            <tbody>
              <tr>
                <td className="text-[color:var(--br-text-dim)] py-1">Depositi</td>
                <td className="text-right text-[color:var(--br-parchment)] py-1" data-testid="cascade-deposits">
                  {preview.deposits}
                </td>
              </tr>
              <tr>
                <td className="text-[color:var(--br-text-dim)] py-1">Estratti</td>
                <td className="text-right text-[color:var(--br-parchment)] py-1" data-testid="cascade-statements">
                  {preview.statements}
                </td>
              </tr>
              <tr>
                <td className="text-[color:var(--br-text-dim)] py-1">Inviti</td>
                <td className="text-right text-[color:var(--br-parchment)] py-1" data-testid="cascade-invites">
                  {preview.invites}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="br-btn-outline"
            data-testid="delete-user-cancel"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="br-btn-primary"
            style={{ background: "var(--br-red)", borderColor: "var(--br-red)", color: "white" }}
            data-testid="delete-user-confirm"
          >
            {loading ? "Eliminazione…" : "Elimina definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-5 py-4 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>
      {children}
    </td>
  );
}


/**
 * Admin password recovery modal — two paths:
 *   1. Send recovery LINK (60-min expiry) via email
 *   2. Generate TEMP PASSWORD (shown ONCE for voice-call hand-off)
 * The user is forced to change a temp password at the next login.
 */
function PasswordResetModal({ target, onClose, onError }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { kind: 'link'|'temp', payload }
  const [copied, setCopied] = useState(false);

  const sendLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/password-reset/link`);
      setResult({ kind: "link", payload: data });
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const generateTemp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/password-reset/temp-password`);
      setResult({ kind: "temp", payload: data });
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyTemp = async () => {
    const pwd = result?.payload?.temp_password;
    if (!pwd) return;
    try {
      await navigator.clipboard.writeText(pwd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = pwd;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-10 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="pwreset-modal"
    >
      <div
        className="br-card relative max-w-[560px] w-full px-8 py-9"
        onClick={(e) => e.stopPropagation()}
        data-testid="pwreset-modal-card"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-6 font-mono-fin text-[0.7rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)] hover:text-[color:var(--br-bronze-soft)] transition-colors"
          data-testid="pwreset-close"
        >
          ✕ Chiudi
        </button>

        <div className="br-overline">Recupero password</div>
        <h2 className="font-serif-display text-[1.6rem] mt-2 leading-tight">
          Reset per <em>{target.name || target.email}</em>
        </h2>
        <p className="mt-2 text-[0.78rem] font-mono-fin text-[color:var(--br-text-dim)]">
          {target.email}
        </p>

        {!result && (
          <div className="mt-6 space-y-4">
            <div
              className="br-card p-4 cursor-pointer hover:border-[color:var(--br-bronze)] transition-colors"
              style={{ background: "rgba(167,128,73,0.04)" }}
              onClick={busy ? undefined : sendLink}
              data-testid="pwreset-option-link"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-serif-display text-[1.05rem] italic">
                    Invia link di recupero via email
                  </div>
                  <p className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-2 leading-relaxed">
                    Genero un token monouso e lo invio all'email del cliente.
                    Validità 60 minuti. Il cliente sceglie la propria nuova password.
                  </p>
                </div>
                <button
                  disabled={busy}
                  className="br-btn-primary"
                  style={{ padding: "8px 16px", fontSize: "0.7rem", whiteSpace: "nowrap" }}
                  data-testid="pwreset-send-link"
                >
                  {busy ? "Invio…" : "Invia link"}
                </button>
              </div>
            </div>

            <div
              className="br-card p-4 cursor-pointer hover:border-[color:var(--br-bronze)] transition-colors"
              style={{ background: "rgba(167,128,73,0.04)" }}
              onClick={busy ? undefined : generateTemp}
              data-testid="pwreset-option-temp"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-serif-display text-[1.05rem] italic">
                    Genera password temporanea
                  </div>
                  <p className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-2 leading-relaxed">
                    Genero una password forte mostrata UNA volta. Da comunicare
                    al cliente in voice call. Il cliente è obbligato a cambiarla
                    al primo accesso.
                  </p>
                </div>
                <button
                  disabled={busy}
                  className="br-btn-outline"
                  style={{ padding: "8px 16px", fontSize: "0.7rem", whiteSpace: "nowrap" }}
                  data-testid="pwreset-generate-temp"
                >
                  {busy ? "Generazione…" : "Genera"}
                </button>
              </div>
            </div>

            <p className="text-[0.72rem] text-[color:var(--br-text-dim)] leading-relaxed pt-2">
              Ogni azione è registrata in audit log e visibile in{" "}
              <span style={{ color: "var(--br-bronze)" }}>/admin/password-audit</span>.
            </p>
          </div>
        )}

        {result?.kind === "link" && (
          <div
            className="mt-6 br-card p-5 br-fade-up"
            style={{ borderLeft: "2px solid var(--br-bronze)", background: "rgba(167,128,73,0.06)" }}
            data-testid="pwreset-link-result"
          >
            <div className="br-overline">Link inviato ✓</div>
            <p className="mt-3 text-[0.9rem] text-[color:var(--br-text)] leading-relaxed">
              Email di recupero spedita a <strong>{target.email}</strong>. Il
              link scade tra {result.payload.ttl_minutes} minuti e può essere
              usato una sola volta.
            </p>
            <button
              onClick={onClose}
              className="br-btn-primary mt-5 w-full"
              data-testid="pwreset-link-done"
            >
              Chiudi
            </button>
          </div>
        )}

        {result?.kind === "temp" && (
          <div
            className="mt-6 br-card p-5 br-fade-up"
            style={{ borderLeft: "2px solid var(--br-bronze)", background: "rgba(167,128,73,0.08)" }}
            data-testid="pwreset-temp-result"
          >
            <div className="br-overline">Password temporanea generata</div>
            <p className="mt-3 text-[0.85rem] text-[color:var(--br-text)] leading-relaxed">
              Comunica questa password al cliente <strong>durante la verifica
              vocale</strong>. Non sarà più mostrata in chiaro. Il cliente è
              obbligato a cambiarla al primo accesso.
            </p>
            <div
              className="mt-4 p-4 font-mono-fin text-[1.2rem] tracking-[0.18em] text-center select-all"
              style={{
                background: "var(--br-bg-inner)",
                border: "1px dashed var(--br-bronze)",
                color: "var(--br-parchment)",
                letterSpacing: "0.18em",
                wordBreak: "break-all",
              }}
              data-testid="pwreset-temp-value"
            >
              {result.payload.temp_password}
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={copyTemp}
                className="br-btn-outline flex-1"
                data-testid="pwreset-temp-copy"
              >
                {copied ? "Copiato ✓" : "Copia"}
              </button>
              <button
                onClick={onClose}
                className="br-btn-primary flex-1"
                data-testid="pwreset-temp-done"
              >
                Fatto
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
